import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';
import { FormField } from '@/models';
import { makeFormFieldOfType } from '@/helpers';

interface PersonalizationContentForm {
  enableEmailMarketing: FormField<boolean | null>;
  enablePersonalizedContent: FormField<boolean | null>;
}

const getFormDefaults = (form: PersonalizationContentForm): PersonalizationContentForm => {
  form.enableEmailMarketing = makeFormFieldOfType<boolean | null>(null);
  form.enablePersonalizedContent = makeFormFieldOfType<boolean | null>(null);

  return form;
};

const personalizationContentForm: PersonalizationContentForm = getFormDefaults({} as PersonalizationContentForm);

@Component({
  name: 'CmgPersonalizedContentModal'
})

export default class CmgPersonalizedContentModal extends Vue {
  personalizationForm = personalizationContentForm;
  isFormValid = true;
  isLoading = false;

  get form(): PersonalizationContentForm {
    return this.personalizationForm;
  }

  get visible(): boolean {
    return this.$appStore.state.ui.modals.personalizedContent.visible;
  }

  get isMobile(): boolean {
    return this.$services.ui.isMobile();
  }

  get args() {
    return this.$appStore.state.ui.modals.personalizedContent.args;
  }

  get showEmailMarketingOptions() {
    return this.$appStore.state.ui.modals.personalizedContent.args?.showEmailMarketingOptions;
  }

  get emailMarketingText() {
    return 'By selecting yes you are granting Chipotle permission to send you mail ' +
      'messages at the email address associated with your account regarding ' +
      'marketing and other promotional offers.'
  }

  get personalizedContentText() {
    return 'By selecting yes you are granting Chipotle permission to use automated ' +
      'processing off your personal information and other data, including ' +
      'order history and data obtained from your interactions with Chipotle, ' +
      'in order to provide you with personalized content and offers for the ' +
      'best experience possible.'
  }

  @Watch('visible', { immediate: true })
  async visibleChanged(newVisible: boolean) {
    if (newVisible) {
      this.resetForm();
    }
  }

  setEnableEmailMarketing(enabled: boolean): void {
    this.form.enableEmailMarketing.error = '';
    this.form.enableEmailMarketing.dirty = true;
    this.form.enableEmailMarketing.value = enabled;
  }

  setEnablePersonalizedContent(enabled: boolean): void {
    this.form.enablePersonalizedContent.error = '';
    this.form.enablePersonalizedContent.dirty = true;
    this.form.enablePersonalizedContent.value = enabled;
  }

  async save(): Promise<void> {
    this.isLoading = true;
    this.validateForm();
    if (!this.isFormValid) {
      this.isLoading = false;
      return;
    }

    this.$services.customer.setOptedIntoPersonalizedMessaging(this.form.enablePersonalizedContent.value || false);
    if (this.showEmailMarketingOptions) {
      this.$services.customer.setOptedIntoEmailMarketingFromOptions(this.form.enableEmailMarketing.value || false);
    }
    if (this.args && this.args.onSuccess) {
      await this.args.onSuccess();
    }
    this.isLoading = false;
    this.close();
  }

  onClose() {
    if (this.args && this.args.onClose) {
      this.args.onClose('');
    }

    this.close();
  }

  close() {
    this.$services.ui.setModalVisibility('personalizedContent', false);
  }

  private resetForm(): void {
    getFormDefaults(this.personalizationForm);
  }

  validateForm() {
    this.isFormValid = true;

    this.validateReceiveEmailPromos();
    this.validatePersonalizationContent();

    for (const key of Object.keys(this.form) as Array<keyof PersonalizationContentForm>) {
      if (!!this.form[key].error) {
        this.form[key].dirty = true;
        this.isFormValid = false;
      }
    }
  }

  validateReceiveEmailPromos(): void {
    if (this.showEmailMarketingOptions) {
      this.form.enableEmailMarketing.error = this.form.enableEmailMarketing.value === null ? 'Please make a selection' : null;
    }
  }

  validatePersonalizationContent(): void {
    this.form.enablePersonalizedContent.error = this.form.enablePersonalizedContent.value === null ? 'Please make a selection' : null;
  }

  privacyLinkClicked(): void {
    this.isLoading = true;
    if (this.args && this.args.onClose) {
      this.args.onClose('/privacy-policy');
    }
  }
}